import { checkConsistency, type Issue, type StoryCtx } from '@dispositio/core';

/**
 * Hinweise einer Geschichte (core `checkConsistency`), einmal je Stand berechnet: Werkzeugleiste, Binder, Erzähllinie, Fläche und
 * Seitenleiste fragen denselben Stand (ein neuer Stand ist ein neuer Kontext).
 */
const cache = new WeakMap<StoryCtx, Issue[]>();
export function issuesOf(ctx: StoryCtx): Issue[] {
  let issues = cache.get(ctx);
  if (!issues) cache.set(ctx, (issues = checkConsistency(ctx)));
  return issues;
}

/**
 * Widersprüche je Node (PF 54): die Hinweise der Art „Prüfen“, die an einem Node hängen – sie bekommen dort einen roten Punkt mit Zahl.
 * Hinweise der Art „Hinweis“ (fehlende Verweise, unerwähnte Verbindungen) sind Vorschläge und zählen nicht.
 */
const byNodeCache = new WeakMap<StoryCtx, ReadonlyMap<string, Issue[]>>();
export function contradictionsOf(ctx: StoryCtx): ReadonlyMap<string, Issue[]> {
  let map = byNodeCache.get(ctx);
  if (!map) {
    const m = new Map<string, Issue[]>();
    for (const i of issuesOf(ctx)) if (i.severity === 'warn') m.set(i.nodeId, [...(m.get(i.nodeId) ?? []), i]);
    byNodeCache.set(ctx, (map = m));
  }
  return map;
}

/** Zahl der Widersprüche je Node (für die Fläche) */
const countCache = new WeakMap<StoryCtx, ReadonlyMap<string, number>>();
export function contradictionCounts(ctx: StoryCtx): ReadonlyMap<string, number> {
  let counts = countCache.get(ctx);
  if (!counts) countCache.set(ctx, (counts = new Map([...contradictionsOf(ctx)].map(([id, list]) => [id, list.length]))));
  return counts;
}

/**
 * Wohin ein Hinweis führt: in das Dokument mit der Stelle (fehlende Verweise mit Textmarker), sonst zum Node; Beziehungen (AP-44) zur
 * Entität – dort steht der Hinweis an der Beziehung, mit dem Weg zur Szene.
 */
export const issueTarget = (i: Issue): { nodeId: string | null; entityId?: string; docId?: string; review?: boolean } =>
  i.docId ? { nodeId: i.nodeId, docId: i.docId, review: i.code === 'missing-ref' } : i.relationId && i.entityId ? { nodeId: null, entityId: i.entityId } : { nodeId: i.nodeId };

/** Hinweise je Beziehung (AP-44): die Seitenleiste einer Entität zeigt sie an der Beziehung */
const byRelationCache = new WeakMap<StoryCtx, ReadonlyMap<string, Issue[]>>();
export function relationIssuesOf(ctx: StoryCtx): ReadonlyMap<string, Issue[]> {
  let map = byRelationCache.get(ctx);
  if (!map) {
    const m = new Map<string, Issue[]>();
    for (const i of issuesOf(ctx)) if (i.relationId) m.set(i.relationId, [...(m.get(i.relationId) ?? []), i]);
    byRelationCache.set(ctx, (map = m));
  }
  return map;
}
